import { useCallback, useMemo, useState } from 'react';
import {
  DtoolsImportContext,
  type DtoolsImportContextValue,
  type SelectedProject,
  useDtoolsImport,
} from 'src/components/ImportDtoolsProjectModal/dtools-import-context';
import { ProjectsSummary } from 'src/components/ImportDtoolsProjectModal/projects-summary';
import { DtoolsImportRows } from 'src/components/ImportProposalsModal/import-rows';
import { ImportSelectStep } from 'src/components/ImportProposalsModal/import-select-step';
import type { ImportListRow } from 'src/components/ImportProposalsModal/use-import-list-rows';
import { ModalContainer, useModalSteps } from 'src/components/ModalContainer';
import { useDtoolsProjectImportJobOutcome } from 'src/graphql/entities/integrations/dtools/hooks/use-dtools-project-import-job-outcome';
import { useStartDtoolsProjectImportJob } from 'src/graphql/entities/integrations/dtools/hooks/use-start-dtools-project-import-job';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';

/** Step 1: the D-Tools Cloud projects to import. */
const DtoolsSelectStep = () => {
  const { pushStep } = useModalSteps();
  const { review, isStartingJob, clearPreview, clearImport, close } = useDtoolsImport();

  const handleReview = async (selected: ImportListRow[]) => {
    if (!(await review(selected))) {
      return;
    }
    await pushStep(<ProjectsSummary />);
    clearPreview();
    clearImport();
  };

  return (
    <ImportSelectStep
      title="Import from D-Tools Cloud"
      Rows={DtoolsImportRows}
      onReview={(selected) => void handleReview(selected)}
      reviewLoading={isStartingJob}
      onCancel={close}
      testIdPrefix="import-dtools-projects"
    />
  );
};

/** Import D-Tools Cloud projects: select, preview a dry run, import, see the results. */
export const ImportDtoolsProjectModal = () => {
  const { isOpened } = useModalState(Modals.ImportDtoolsProjects);
  const { closeModal } = useModalMethods(Modals.ImportDtoolsProjects);
  const { showInfoAlert } = useSnackbar();
  const { startJob, loading: isStartingJob } = useStartDtoolsProjectImportJob();

  const [previewProjects, setPreviewProjects] = useState<SelectedProject[]>([]);
  const [previewJobId, setPreviewJobId] = useState<string | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);

  const previewOutcome = useDtoolsProjectImportJobOutcome(previewJobId);
  const importOutcome = useDtoolsProjectImportJobOutcome(importJobId);
  const isImportRunning = Boolean(importJobId) && !importOutcome.isFinished;

  // Closing never waits for a job: the import goes on in the background.
  const close = useCallback(() => {
    if (isStartingJob || isImportRunning) {
      showInfoAlert(
        'The import keeps running in the background. The projects will appear once it finishes.',
      );
    }
    closeModal();
  }, [closeModal, isImportRunning, isStartingJob, showInfoAlert]);

  const context = useMemo<DtoolsImportContextValue>(
    () => ({
      previewProjects,
      previewOutcome,
      importOutcome,
      isStartingJob,
      hasImportStarted: Boolean(importJobId),
      review: async (projects) => {
        const job = await startJob({
          projectIds: projects.map((project) => project.id.toString()),
          dryRun: true,
        });
        if (!job) {
          return false;
        }
        setPreviewProjects(projects);
        setPreviewJobId(job.jobId);
        return true;
      },
      importPreviewed: async () => {
        const job = await startJob({
          projectIds: previewProjects.map((project) => project.id.toString()),
          dryRun: false,
        });
        if (!job) {
          return false;
        }
        setImportJobId(job.jobId);
        return true;
      },
      clearPreview: () => setPreviewJobId(null),
      clearImport: () => setImportJobId(null),
      close,
    }),
    [
      close,
      importJobId,
      importOutcome,
      isStartingJob,
      previewOutcome,
      previewProjects,
      startJob,
    ],
  );

  return (
    <DtoolsImportContext.Provider value={context}>
      <ModalContainer
        open={isOpened}
        size="large"
        onClose={close}
        data-testid="import-dtools-projects-modal"
      >
        <DtoolsSelectStep />
      </ModalContainer>
    </DtoolsImportContext.Provider>
  );
};
